import React, { useState } from 'react';
import { VideoIcon } from '../icons';
import Logo from '../Logo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 4;

export default function PlayerForm({ courtName, onStart }) {
  // emails[0] is the primary recipient; the rest are optional extras.
  const [emails, setEmails] = useState(['']);
  const [errors, setErrors] = useState({});

  const updateEmail = (idx, val) => {
    const next = [...emails];
    next[idx] = val;
    setEmails(next);
    setErrors(p => ({ ...p, [idx]: null, dup_general: null }));
  };

  const addEmailField = () => {
    if (emails.length >= MAX_EMAILS) return;
    setEmails([...emails, '']);
  };

  const removeEmailField = (idx) => {
    if (emails.length <= 1) return;
    setEmails(emails.filter((_, i) => i !== idx));
    setErrors({});
  };

  const validate = () => {
    const e = {};
    const seen = new Map(); // normalized -> first index

    emails.forEach((addr, i) => {
      const trimmed = addr.trim();
      if (i === 0 && !trimmed) {
        e[0] = 'Please enter your email';
        return;
      }
      if (!trimmed) return; // blank optional slots are fine, just ignored
      if (!EMAIL_RE.test(trimmed)) {
        e[i] = 'Please enter a valid email address';
        return;
      }
      const norm = trimmed.toLowerCase();
      if (seen.has(norm)) {
        e[i] = 'This email was already added';
      } else {
        seen.set(norm, i);
      }
    });

    return e;
  };

  const handleStart = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    const cleaned = emails.map(a => a.trim().toLowerCase()).filter(Boolean);
    const [primary, ...rest] = cleaned;
    const extras = [...new Set(rest)].filter(a => a !== primary);

    onStart(primary, extras);
  };

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topBar}>
        <span style={s.logo}><Logo size={22} style={{ marginRight: 8, verticalAlign: -6 }} />Picklers Lounge</span>
        <span style={s.courtChip}>{courtName}</span>
      </div>

      {/* Main card */}
      <div style={s.center}>
        <div style={s.card} className="fade-in">
          <div style={s.cardTop}>
            <div style={s.bigIcon}><VideoIcon size={40} color="var(--blue)" /></div>
            <h1 style={s.title}>Record Your Match</h1>
            <p style={s.subtitle}>
              Enter your email and we'll send the video when you stop recording.
              Add teammates to send them a copy too.
            </p>
          </div>

          <div style={s.fields}>
            {emails.map((addr, i) => (
              <div key={i} style={s.field}>
                <label style={s.label}>
                  {i === 0 ? (
                    <>Your Email <span style={s.badge}>Primary</span></>
                  ) : (
                    `Email ${i + 1}`
                  )}
                </label>
                <div style={s.inputRow}>
                  <input
                    type="email"
                    value={addr}
                    onChange={e => updateEmail(i, e.target.value)}
                    placeholder={i === 0 ? 'you@example.com' : `teammate${i + 1}@example.com`}
                    style={{ ...s.input, ...(errors[i] ? s.inputErr : {}) }}
                    autoComplete="email"
                    inputMode="email"
                    autoFocus={i === 0}
                  />
                  {i > 0 && (
                    <button
                      type="button"
                      onClick={() => removeEmailField(i)}
                      style={s.removeBtn}
                      title="Remove this email"
                      aria-label="Remove this email"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {errors[i] && <span style={s.fieldErr}>{errors[i]}</span>}
              </div>
            ))}

            {emails.length < MAX_EMAILS && (
              <button type="button" onClick={addEmailField} style={s.addBtn}>
                + Add another email
              </button>
            )}
          </div>

          <button
            onClick={handleStart}
            style={s.startBtn}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-dark)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--blue)'}
          >
            <span style={s.recDot} />
            Start Recording
          </button>

          <p style={s.hint}>
            Your video will be emailed automatically when you tap Stop.
          </p>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: 'var(--gray-50)',
    display: 'flex', flexDirection: 'column',
    fontFamily: 'var(--font)',
  },
  topBar: {
    height: 60, padding: '0 28px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'var(--white)', borderBottom: '1px solid var(--gray-200)',
    boxShadow: 'var(--shadow-sm)',
  },
  logo: { fontSize: 17, fontWeight: 700, color: 'var(--gray-900)' },
  courtChip: {
    background: 'var(--blue-light)', color: 'var(--blue)',
    fontSize: 13, fontWeight: 600,
    padding: '5px 14px', borderRadius: 50,
  },
  center: {
    flex: 1, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    padding: '32px 20px',
  },
  card: {
    background: 'var(--white)', borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    padding: '44px 40px', width: '100%', maxWidth: 460,
  },
  cardTop: { textAlign: 'center', marginBottom: 36 },
  bigIcon: { marginBottom: 16, display: 'flex', justifyContent: 'center' },
  title: {
    fontSize: 28, fontWeight: 700, color: 'var(--gray-900)',
    letterSpacing: '-0.5px', marginBottom: 10,
  },
  subtitle: {
    fontSize: 15, color: 'var(--gray-500)',
    lineHeight: 1.6, maxWidth: 340, margin: '0 auto',
  },
  fields: { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--gray-700)', display: 'flex', alignItems: 'center', gap: 6 },
  badge: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
    background: 'var(--blue-light)', color: 'var(--blue)',
    padding: '2px 7px', borderRadius: 20,
  },
  inputRow: { display: 'flex', gap: 8, alignItems: 'center' },
  input: {
    flex: 1,
    padding: '13px 15px', fontSize: 16,
    border: '1.5px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--gray-900)', outline: 'none',
    transition: 'border-color 0.15s',
    background: 'var(--white)',
  },
  inputErr: { borderColor: 'var(--red)' },
  fieldErr: { fontSize: 12, color: 'var(--red)', fontWeight: 500 },
  removeBtn: {
    flexShrink: 0, width: 40, height: 44,
    background: 'var(--gray-100)', border: '1.5px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)', color: 'var(--gray-500)',
    fontSize: 15, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  addBtn: {
    alignSelf: 'flex-start',
    background: 'none', border: 'none',
    color: 'var(--blue)', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', padding: '6px 2px',
    fontFamily: 'var(--font)',
  },
  startBtn: {
    width: '100%', padding: '16px',
    background: 'var(--blue)', color: 'white',
    border: 'none', borderRadius: 'var(--radius-sm)',
    fontSize: 17, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    transition: 'background 0.15s',
    boxShadow: '0 4px 14px rgba(26,107,255,0.35)',
    marginBottom: 16,
    cursor: 'pointer',
  },
  recDot: {
    width: 10, height: 10, borderRadius: '50%',
    background: 'white', opacity: 0.9, flexShrink: 0,
  },
  hint: {
    textAlign: 'center', fontSize: 13,
    color: 'var(--gray-400)', lineHeight: 1.5,
  },
};
